import React, { useState, useEffect } from 'react';
import {
    Snackbar,
    Button,
    Box,
    Typography,
    IconButton,
    Paper,
} from '@mui/material';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import CloseIcon from '@mui/icons-material/Close';

const DISMISSED_KEY = 'happyears-pwa-install-dismissed';

/**
 * InstallPWAPrompt — captures the browser's beforeinstallprompt event and shows
 * a non-intrusive "Add to Home Screen / Install App" banner.
 *
 * - Dismissed state persists in sessionStorage so users are not nagged on page reload.
 * - Only visible when the browser provides the install prompt (not already installed).
 */
const InstallPWAPrompt = () => {
    const [installPrompt, setInstallPrompt] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(DISMISSED_KEY)) {
            return;
        }

        const handler = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
            setOpen(true);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        if (!installPrompt) return;
        try {
            await installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            if (outcome === 'accepted') {
                setOpen(false);
                setInstallPrompt(null);
            }
        } catch (_) {
            // prompt unavailable — silently ignore
        }
    };

    const handleDismiss = () => {
        sessionStorage.setItem(DISMISSED_KEY, '1');
        setOpen(false);
    };

    if (!installPrompt) return null;

    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ mb: { xs: 8, sm: 2 } }}
        >
            <Paper
                elevation={6}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    maxWidth: 420,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <InstallMobileIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
                        Install Happy Ears
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Add to home screen for faster access
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleInstall}
                    sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}
                >
                    Install
                </Button>
                <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss install prompt">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Paper>
        </Snackbar>
    );
};

export default InstallPWAPrompt;
